import { useEffect } from "react";

import { useFetchAgentThreads } from "@/lib/components/Menu/components/Threads/hooks/useFetchAgentThreads";
import { THREADS_REFRESH_INTERVAL_MS } from "@/lib/constants/threads";
import { useChatsContext } from "@/lib/context/ChatsProvider/hooks/useChatsContext";
import { useAuth } from "@/lib/hooks";
import { ThreadStatus } from "@/lib/types/Thread";

import { ThreadStatusCounts } from "./ThreadStatusCounts";

const MENU_STATUSES = [ThreadStatus.InputRequired, ThreadStatus.Error];

type MenuThreadStatusCountsProps = {
  /** The Threads list fetches (and refreshes) counts itself while mounted. */
  isThreadsListMounted: boolean;
};

export const MenuThreadStatusCounts = ({
  isThreadsListMounted,
}: MenuThreadStatusCountsProps): JSX.Element => {
  const { statusCounts, threadsRefreshToken } = useChatsContext();
  const { fetchThreads } = useFetchAgentThreads();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const hasCounts = statusCounts !== undefined;

  useEffect(() => {
    if (isThreadsListMounted || !isAuthenticated || isAuthLoading) return;

    if (!hasCounts) void fetchThreads();
    const interval = setInterval(
      () => void fetchThreads(),
      THREADS_REFRESH_INTERVAL_MS,
    );

    return () => clearInterval(interval);
    // hasCounts only gates the initial fetch; don't restart the interval on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchThreads, isAuthenticated, isAuthLoading, isThreadsListMounted]);

  useEffect(() => {
    if (!threadsRefreshToken || isThreadsListMounted) return;
    if (!isAuthenticated || isAuthLoading) return;

    void fetchThreads();
    // Only refetch on explicit refresh requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadsRefreshToken]);

  return (
    <ThreadStatusCounts
      counts={statusCounts}
      statuses={MENU_STATUSES}
      dataTest="overview-statuses"
    />
  );
};
